import { useBreakpoints } from '@assets/scripts/composables/useBreakpoints.js'

const breakpoints = useBreakpoints()

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('#drawer')
  const burgerBtn = document.querySelector('#drawer-open')
  const closeBtn = drawer?.querySelector('#drawer-close')
  const backdrop = document.querySelector('.backdrop')

  if (!burgerBtn || !drawer || !closeBtn || !backdrop) return

  const OPEN_CLASS = 'is-open'
  const VISIBLE_CLASS = 'is-visible'
  const DARK_BG_CLASS = 'bg-dark'

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')

  let lastFocused = null

  const setInert = (el, inert) => {
    if ('inert' in el) {
      el.inert = !!inert
    } else {
      if (inert) el.setAttribute('aria-hidden', 'true')
      else el.removeAttribute('aria-hidden')
    }
  }

  const setWaiAttrs = (inert = false) => {
    if (inert) {
      drawer.removeAttribute('role')
      drawer.removeAttribute('aria-modal')
      drawer.removeAttribute('aria-label')
      burgerBtn.removeAttribute('aria-expanded')
    } else {
      drawer.setAttribute('role', 'dialog')
      drawer.setAttribute('aria-modal', 'true')
      drawer.setAttribute('aria-label', 'Modal menu')
      burgerBtn.setAttribute('aria-expanded', 'false')
    }
  }

  const getFocusable = (root) => Array.from(root.querySelectorAll(focusableSelector))

  const lockScroll = () => {
    document.documentElement.classList.add('no-scroll')
    document.body.classList.add('no-scroll')
  }
  const unlockScroll = () => {
    document.documentElement.classList.remove('no-scroll')
    document.body.classList.remove('no-scroll')
  }

  const openDrawer = () => {
    lockScroll()

    burgerBtn.setAttribute('aria-expanded', 'true')

    drawer.style.transition = 'transform 1s'
    backdrop.style.display = 'block'
    setTimeout(() => {
      drawer.classList.add(OPEN_CLASS)
      backdrop.classList.add(VISIBLE_CLASS)
    }, 10)

    const focusables = getFocusable(drawer)
    const first = focusables[0] || closeBtn;
    (first || drawer).focus({ preventScroll: true })

    document.addEventListener('keydown', onKeydown, true)

    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
  }

  const closeDrawer = () => {
    unlockScroll()

    if (burgerBtn.getAttribute('aria-expanded')) {
      burgerBtn.setAttribute('aria-expanded', 'false')
    }

    if (drawer.contains(document.activeElement)) {
      document.activeElement.blur()
    }

    drawer.classList.remove(OPEN_CLASS)
    backdrop.classList.remove(VISIBLE_CLASS)

    setTimeout(() => {
      drawer.style.transition = 'none'
      backdrop.style.display = 'none'
    }, 1000)

    document.removeEventListener('keydown', onKeydown, true)

    if (lastFocused && document.contains(lastFocused)) {
      lastFocused.focus({ preventScroll: true })
    } else {
      burgerBtn.focus({ preventScroll: true })
    }
  }

  const onKeydown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      closeDrawer()
      return
    }
    if (e.key !== 'Tab') return

    const focusables = getFocusable(drawer).filter(el => el.offsetParent !== null || el === document.activeElement)
    if (focusables.length === 0) {
      e.preventDefault()
      drawer.focus()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const current = document.activeElement

    if (e.shiftKey) {
      if (current === first || !drawer.contains(current)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (current === last || !drawer.contains(current)) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  const tabletSm = breakpoints.get('tablet-sm')
  const media = window.matchMedia(`(min-width: ${tabletSm}px)`)

  const handleResize = () => {
    if (media.matches) {
      setWaiAttrs(true)
      setInert(burgerBtn, true)
      closeDrawer()
      drawer.classList.remove(DARK_BG_CLASS)
    } else {
      setWaiAttrs()
      setInert(burgerBtn, false)
      drawer.classList.add(DARK_BG_CLASS)
    }
  }

  media.addEventListener('change', handleResize)
  handleResize()

  burgerBtn.addEventListener('click', (e) => {
    e.preventDefault()
    openDrawer()
  })

  closeBtn.addEventListener('click', (e) => {
    e.preventDefault()
    closeDrawer()
  })

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeDrawer()
  })

  drawer.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]')
    if (link && !media.matches) closeDrawer()
  })
})
